import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect } from 'react';
import { reportError } from '../utils/errorReporting';

const POSTHOG_CONFIG = {
  api_host: 'https://analytics.fretgenius.app', // Custom domain via Cloudflare proxy
  disable_persistence: true,
  capture_pageview: true,
  autocapture: true,
  mask_all_element_attributes: true,
  mask_all_text: true,
} as const;

// Constants for internal user check
const internalUserFlagKey = 'isInternalPosthogUser';
const urlParamKey = 'internal_user';

// New implementation without cookies or consent banner
const PostHogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useEffect(() => {
    // Initialize PostHog immediately without consent banner
    initializePostHog();
  }, []);

  const initializePostHog = () => {
    // Skip PostHog in development mode
    if (import.meta.env.DEV) {
      console.log('PostHog: Analytics disabled in development mode');
      return;
    }

    let isInternal = localStorage.getItem(internalUserFlagKey) === 'true';
    if (!isInternal) {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get(urlParamKey) === 'true') {
          console.log(
            'PostHog: Internal user detected via URL parameter. Setting flag and disabling tracking.'
          );
          localStorage.setItem(internalUserFlagKey, 'true');
          isInternal = true;
        }
      } catch (e) {
        reportError('PostHog: Error processing URL parameters for internal user check', {
          source: 'PostHogProvider',
          context: 'internalUserCheck',
          error: e,
        });
      }
    }

    if (isInternal) {
      console.log('PostHog: Initialization skipped for internal user.');
      return; // Don't initialize if internal user
    }

    // Get the PostHog key and ensure it's a string
    const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
    if (typeof posthogKey === 'string') {
      posthog.init(posthogKey, {
        ...POSTHOG_CONFIG,
        loaded: (ph) => {
          ph.register({ platform: 'web' }); // Add platform property globally
        },
      });
    } else {
      reportError('PostHog key is not properly configured', {
        source: 'PostHogProvider',
        check: 'import.meta.env.VITE_POSTHOG_KEY',
      });
    }
  };

  return <PHProvider client={posthog}>{children}</PHProvider>;
};

export { PostHogProvider };

/* -- ORIGINAL CONSENT-BASED CODE (KEPT FOR FUTURE REFERENCE) --

interface CookieBannerProps {
  onAccept: () => void;
  onDecline: () => void;
}

const CookieBanner: React.FC<CookieBannerProps> = ({ onAccept, onDecline }) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-4 z-50 shadow-lg border-t border-gray-700 flex flex-wrap justify-center items-center gap-4">
      <div className="text-left">
        This site uses cookies for analytics to understand how it's used and improve FretGenius.{' '}
        <a
          href="https://fretgenius.app/privacy"
          target="_blank"
          className="text-cyan-400 hover:text-cyan-300 underline"
        >
          Learn more
        </a>
        .
      </div>
      <div className="flex space-x-2">
        <button
          className="bg-cyan-500 hover:bg-cyan-600 text-white py-2 px-4 rounded shadow transition-colors duration-200"
          onClick={onAccept}
        >
          Accept
        </button>
        <button
          className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded shadow transition-colors duration-200"
          onClick={onDecline}
        >
          Decline
        </button>
      </div>
    </div>
  );
};

const ManageCookiesLink: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <a
      href="#"
      className="text-gray-300 hover:text-gray-100 ml-4 text-sm"
      onClick={(e) => {
        e.preventDefault();
        onClick();
      }}
    >
      Manage Cookies
    </a>
  );
};

const CONSENT_KEY = 'cookieConsent';

// Cookie utility functions
const getCookie = (name: string): string | null => {
  const cookies = document.cookie.split('; ');
  const cookie = cookies.find((c) => c.startsWith(name + '='));
  return cookie ? cookie.split('=')[1] : null;
};

const setCookie = (name: string, value: string, days = 365): void => {
  const domain = '.fretgenius.app';
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value}; domain=${domain}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`;
};

const deleteCookie = (name: string): void => {
  const domain = '.fretgenius.app';
  document.cookie = `${name}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

const PostHogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [footerElement, setFooterElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Check for existing consent (first try cookie, then fallback to localStorage for backward compatibility)
    const consent = getCookie(CONSENT_KEY) || localStorage.getItem(CONSENT_KEY);

    if (consent === null) {
      setShowBanner(true);
    } else if (consent === 'accepted') {
      // Also set the cookie to ensure future visits use the cookie approach
      setCookie(CONSENT_KEY, 'accepted');
      initializePostHog();
    } else if (consent === 'declined') {
      setCookie(CONSENT_KEY, 'declined');
    }

    // Find the footer element for the manage cookies link
    const footer = document.querySelector('footer');
    if (footer instanceof HTMLElement) {
      setFooterElement(footer);
    }

    // Cleanup function
    return () => {
      setFooterElement(null);
    };
  }, []);

  const initializePostHog = () => {
    // Skip PostHog in development mode
    if (import.meta.env.DEV) {
      console.log('PostHog analytics disabled in development mode');
      return;
    }

    // Get the PostHog key and ensure it's a string
    const posthogKey = import.meta.env.VITE_POSTHOG_KEY;
    if (typeof posthogKey === 'string') {
      posthog.init(posthogKey, POSTHOG_CONFIG);
    } else {
      // reportError('PostHog key is not properly configured'); // Replaced the console.error here in the commented code
    }
  };

  const handleAccept = () => {
    setCookie(CONSENT_KEY, 'accepted');
    localStorage.setItem(CONSENT_KEY, 'accepted'); // For backward compatibility
    setShowBanner(false);
    initializePostHog();
  };

  const handleDecline = () => {
    setCookie(CONSENT_KEY, 'declined');
    localStorage.setItem(CONSENT_KEY, 'declined'); // For backward compatibility
    setShowBanner(false);
    if (typeof posthog.opt_out_capturing === 'function') {
      posthog.opt_out_capturing();
    }
  };

  const handleManageConsent = () => {
    deleteCookie(CONSENT_KEY);
    localStorage.removeItem(CONSENT_KEY);
    setShowBanner(true);
    if (typeof posthog.opt_out_capturing === 'function') {
      posthog.opt_out_capturing();
    }
  };

  return (
    <>
      <PHProvider client={posthog}>{children}</PHProvider>

      {// Use createPortal for the cookie banner to render it at the document body level
      showBanner &&
        createPortal(
          <CookieBanner onAccept={handleAccept} onDecline={handleDecline} />,
          document.body
        )}

      {// Use createPortal for the manage cookies link when footer exists
      footerElement &&
        createPortal(<ManageCookiesLink onClick={handleManageConsent} />, footerElement)}
    </>
  );
};
*/
